import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { CurrentLangService } from '@core/services/common/current-lang.service';
import { EntityDisplayNamesService } from '@core/services/entity-display-names/entity-display-names.service';
import { printedKind } from '@core/services/gbe-error-parser/printed-kind';
import { WarningPresenter } from '@core/services/gbe-error-parser/warning-presenter.service';
import {
  FormattedField,
  MessagePart,
  NormalizedWarning,
  WarningOccurrence
} from '@core/services/gbe-error-parser/warning.model';
import {
  PlanChangesView,
  selectPlanChanges
} from '@core/store/plan-edit/plan-edit-session.selectors';
import { FindingChangeKind, diffFindings } from '@core/utils/plan-edit/findings-diff';
import { formatCompactDuration } from '@core/utils/time-utils';

import { BadgeComponent, BadgeStatus } from '@shared/components/badge/badge.component';
import {
  commonFindingDay,
  displayedPartText
} from '@shared/components/finding-message/finding-display';
import { FindingMessageComponent } from '@shared/components/finding-message/finding-message.component';

import {
  FindingCardData,
  FindingCardPopupService
} from '../../../finding-card/finding-card-popup.service';
import { PresentedFinding, previousOf } from './finding-previous';

interface ChangeRow {
  kind: FindingChangeKind;
  body: string;
  parts: MessagePart[] | undefined;
  was: string | null;
  card: Omit<FindingCardData, 'typeLabels'>;
}

const TAG_STATUS: Record<FindingChangeKind, BadgeStatus> = {
  new: 'pending',
  changed: 'info',
  gone: 'inactive'
};

/** A changed field as its row shows it: durations compacted, a date-time on the shared day as its time. */
export function shownValue(
  field: FormattedField,
  sameDay: string | null,
  lang: string
): string {
  const raw = field.raw;
  if (raw === undefined) return field.value;
  switch (printedKind(raw, field.value, lang)) {
    case 'duration':
      return formatCompactDuration(raw);
    case 'datetime':
      return displayedPartText({ kind: 'datetime', iso: raw, text: field.value }, sameDay);
    default:
      return field.value;
  }
}

/** The section shows once a live session has a change to report. */
export function changesSectionVisible(changes: PlanChangesView | null): boolean {
  return changes?.lastRound != null;
}

/** What the plan's last change did to its findings, whoever made it. */
@Component({
  selector: 'app-changes-section',
  imports: [BadgeComponent, FindingMessageComponent, TranslatePipe],
  templateUrl: './changes-section.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: ``
})
export class ChangesSectionComponent {
  private readonly presenter = inject(WarningPresenter);
  private readonly lang = inject(CurrentLangService).currentLang;
  private readonly cardPopup = inject(FindingCardPopupService);
  protected readonly typeLabels = inject(EntityDisplayNamesService).singularDisplayNames;
  private readonly changes = inject(Store).selectSignal(selectPlanChanges);
  private readonly lastRound = computed(() => this.changes()?.lastRound ?? null);
  /** The rows are the previous change's until the next one is answered. */
  protected readonly muted = computed(() => this.changes()?.settled === false);

  protected readonly TAG_STATUS = TAG_STATUS;

  private readonly view = computed<{ rows: ChangeRow[]; sameDay: string | null }>(() => {
    this.lang();
    const round = this.lastRound();
    if (!round) return { rows: [], sameDay: null };
    const diffs = diffFindings(
      round.before.findings,
      round.after.findings,
      round.touchedRouteUids
    ).map(({ kind, entry, previous, finding }) => {
      const occurrence = this.presenter.presentEntry(entry);
      return {
        kind,
        entry,
        finding,
        occurrence,
        now: this.present(entry, occurrence),
        before: previous
          ? this.present(previous, this.presenter.presentEntry(previous))
          : null
      };
    });
    const sameDay = commonFindingDay(
      diffs.flatMap(({ now, before }) => (before ? [now, before] : [now]))
    );
    const rows = diffs.map(({ kind, entry, finding, occurrence, now, before }) => ({
      kind,
      body: now.body,
      parts: now.parts,
      was: before
        ? previousOf(now, before, (field) => shownValue(field, sameDay, this.lang()))
        : null,
      card: {
        title: this.presenter.titleOf(entry.code),
        code: entry.code,
        occurrence: { ...occurrence, body: now.body },
        raw: finding
      }
    }));
    return { rows, sameDay };
  });

  protected readonly rows = computed(() => this.view().rows);
  protected readonly sameDay = computed(() => this.view().sameDay);

  /** Toggles closed on a second click of the same row, as the findings list does. */
  protected openCard(row: ChangeRow, event: Event): void {
    const anchorEl = event.currentTarget as HTMLElement;
    if (this.cardPopup.isOpenFor(anchorEl)) {
      this.cardPopup.close();
      return;
    }
    this.cardPopup.open(anchorEl, { ...row.card, typeLabels: this.typeLabels() });
  }

  /**
   * An entry DL describes in no words is still named, by the title of its code. Its parts are
   * dropped with its words: a message renders its parts whenever it has any, empty or not.
   */
  private present(
    entry: NormalizedWarning,
    occurrence: WarningOccurrence
  ): PresentedFinding {
    return occurrence.body
      ? {
          body: occurrence.body,
          parts: occurrence.parts,
          payload: this.presenter.formattedPayload(entry)
        }
      : {
          body: this.presenter.titleOf(entry.code),
          payload: this.presenter.formattedPayload(entry)
        };
  }
}
